import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { AnimatePresence, motion, useDragControls } from "framer-motion";
import { useEffect, useState } from "react";

import { useLogout } from "$/api/logout";
import { useSelectedYear } from "$/lib/hooks/useSelectedYear";
import AidIcon2 from "$/lib/icons/AidIcon2";
import ChevronIcon from "$/lib/icons/ChevronIcon";
import CreditCardFilledIcon from "$/lib/icons/CreditCardFilledIcon";
import GestionIcon from "$/lib/icons/GestionIcon";
import HomeIcon from "$/lib/icons/HomeIcon";
import IdeaIcon from "$/lib/icons/IdeaIcon";
import OptimisationIcon from "$/lib/icons/OptimisationIcon";
import PowerIcon from "$/lib/icons/PowerIcon";
import ProfileIcon from "$/lib/icons/ProfileIcon";
import SettingsIcon from "$/lib/icons/SettingsIcon";
import TransactionIcon from "$/lib/icons/TransactionIcon";
import { useAuth } from "$/lib/providers/AuthProvider";
import { ROUTES } from "$/lib/utils/constants/routes.constants";
import { useProfileProvider } from "$/routes/_user-layout/profile/-features/providers/ProfileProvider";

import NavbarAnimatedText from "./NavbarAnimatedText";
import NavbarBackgroundIndicator from "./NavbarBackgroundIndicator";
import NavbarItemIcon from "./NavbarItemIcon";

const mainNavItems = [
  {
    label: "Accueil",
    icon: HomeIcon,
    to: ROUTES.HOMEPAGE,
    basePath: null as string | null,
  },
  {
    label: "Transactions",
    icon: TransactionIcon,
    to: ROUTES.TRANSACTIONS,
    basePath: null as string | null,
  },
  {
    label: "Gestion",
    icon: GestionIcon,
    to: ROUTES.MANAGEMENT_MYBUSINESS,
    basePath: "/management",
  },
  {
    label: "Optimisation",
    icon: OptimisationIcon,
    to: ROUTES.OPTIMIZATION,
    basePath: null as string | null,
  },
];

const menuItems = [
  {
    label: "Profil",
    icon: ProfileIcon,
    to: ROUTES.PROFILE_GENERAL,
    basePath: "/profile",
  },
  {
    label: "Aide",
    icon: AidIcon2,
    to: ROUTES.HELP_VIDEO,
    basePath: "/help",
  },
  {
    label: "Mon compte",
    icon: SettingsIcon,
    to: ROUTES.USER_INFO,
    basePath: null as string | null,
  },
  {
    label: "Mon abonnement",
    icon: CreditCardFilledIcon,
    to: ROUTES.SUBSCRIPTION,
    basePath: null as string | null,
  },
];

const SUGGESTION_MAILTO = `mailto:contact@libe.app?subject=${encodeURIComponent(
  "Suggestion d'idée / amélioration - Libe app",
)}&body=${encodeURIComponent("Bonjour,\n\n")}`;

export default function MobileNavbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const { mutate: logout, isPending: isLoggingOut } = useLogout();
  const { user } = useAuth();
  const { profileCompletion } = useProfileProvider();
  const { sessionStorageYearValue } = useSelectedYear();
  const sheetDragControls = useDragControls();

  const isActive = (to: string, basePath: string | null) => {
    if (basePath) return location.pathname.startsWith(basePath);
    return location.pathname === to;
  };

  const isMenuActive = menuItems.some(({ to, basePath }) =>
    isActive(to, basePath),
  );

  const socialSecurityYear = new Date().getFullYear() - 2;
  const isSocialProfileMissing =
    profileCompletion?.socialInfoMissingYears.includes(socialSecurityYear) ??
    false;
  const businessStartYear = user
    ? new Date(user.businessStartDate).getFullYear()
    : undefined;
  const isAtLeastThirdYear =
    businessStartYear !== undefined &&
    sessionStorageYearValue - businessStartYear >= 2;
  const showSocialProfileAlert = isAtLeastThirdYear && isSocialProfileMissing;

  // Le menu n'est pas une vraie route : on le referme nous-mêmes dès que
  // l'utilisateur navigue (via un item du menu ou via la navbar), sinon il
  // resterait affiché par-dessus la page suivante (il est en `fixed inset-0`).
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav
        className="fixed bottom-3 left-[3%] right-[3%] z-40 flex items-center rounded-xl bg-navbar-linear-gradient px-1 screen850:hidden"
        style={{ boxShadow: "0 -2px 7px rgba(14, 5, 73, 0.05)" }}
      >
        {mainNavItems.map(({ label, icon: Icon, to, basePath }) => {
          // Quand le menu est ouvert, seul le bouton "Menu" doit apparaître
          // actif : sinon deux indicateurs partageant le même `layoutId`
          // Framer Motion sont montés en même temps, ce qui casse
          // l'animation partagée (le carré se déforme jusqu'au bord).
          const active = !menuOpen && isActive(to, basePath);
          return (
            <Link
              key={label}
              to={to}
              onClick={() => setMenuOpen(false)}
              className="relative h-14 flex-1 cursor-pointer"
              data-onboarding={
                to === ROUTES.TRANSACTIONS
                  ? "transactions-link-mobile"
                  : undefined
              }
            >
              <NavbarItemIcon active={active}>
                <Icon className="h-5 w-5" />
              </NavbarItemIcon>
              {active && (
                <>
                  <NavbarBackgroundIndicator
                    layoutId="mobileNavbarIndicator"
                    heightClassName="h-[88%]"
                  />
                  <NavbarAnimatedText label={label} />
                </>
              )}
            </Link>
          );
        })}

        {/* Menu burger */}
        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          className="relative h-14 flex-1 cursor-pointer"
        >
          <NavbarItemIcon
            active={isMenuActive || menuOpen}
            dotColor={showSocialProfileAlert ? "bg-notif" : undefined}
          >
            <BurgerIcon />
          </NavbarItemIcon>
          {(isMenuActive || menuOpen) && (
            <>
              <NavbarBackgroundIndicator
                layoutId="mobileNavbarIndicator"
                heightClassName="h-[88%]"
              />
              <NavbarAnimatedText label="Menu" />
            </>
          )}
        </button>
      </nav>

      {/* Bottom sheet : overlay assombri + panneau qui coulisse depuis le bas,
          fermable au tap sur l'overlay ou au swipe vers le bas. z-30 (sous la
          navbar en z-40) pour que celle-ci reste visible et cliquable
          par-dessus, comme pour l'ancienne version plein écran. */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              key="mobile-menu-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
              className="fixed inset-0 z-30 bg-black/40 screen850:hidden"
            />
            <motion.div
              key="mobile-menu-sheet"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
              drag="y"
              dragListener={false}
              dragControls={sheetDragControls}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.5 }}
              onDragEnd={(_, info) => {
                if (info.offset.y > 80 || info.velocity.y > 500) {
                  setMenuOpen(false);
                }
              }}
              className="fixed inset-x-0 bottom-0 z-30 rounded-t-2xl bg-[#FFFCFA] px-4 pb-20 pt-3 screen850:hidden"
            >
              <div
                onPointerDown={(e) => sheetDragControls.start(e)}
                className="mx-auto mb-4 h-1 w-10 touch-none rounded-full bg-border"
              />

              <div className="flex flex-col gap-3">
                {menuItems.map(({ label, icon: Icon, to, basePath }) => {
                  const active = isActive(to, basePath);
                  return (
                    <Link
                      key={label}
                      to={to}
                      onClick={() => setMenuOpen(false)}
                      className={`flex items-center justify-between rounded-xl border px-4 py-3.5 shadow-xs transition-colors ${
                        active
                          ? "border-gris-300 bg-gris-100 text-textColor"
                          : "border-border bg-white text-textColor hover:bg-gris-50"
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <Icon className="h-5 w-5 shrink-0" />
                        <span className="text-sm font-medium">{label}</span>
                        {label === "Profil" && showSocialProfileAlert && (
                          <span className="flex items-center gap-1.5 rounded-full bg-bgnotif border border-notif/50  px-2.5 py-1">
                            <span className="h-2 w-2 shrink-0 rounded-full bg-notif" />
                            <span className="text-[13px] font-medium text-textColor">
                              Profil social {socialSecurityYear} requis
                            </span>
                          </span>
                        )}
                      </span>
                      <ChevronIcon className="h-3 w-2 shrink-0 opacity-60" />
                    </Link>
                  );
                })}

                <a
                  href={SUGGESTION_MAILTO}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center justify-between rounded-xl border border-border bg-white px-4 py-3.5 text-textColor shadow-xs transition-colors hover:bg-gris-50"
                >
                  <span className="flex items-center gap-3">
                    <IdeaIcon className="h-5 w-5 shrink-0" />
                    <span className="text-sm font-medium">
                      Discuter avec nous / Suggérer une idée
                    </span>
                  </span>
                  <ChevronIcon className="h-3 w-2 shrink-0 opacity-60" />
                </a>

                <button
                  type="button"
                  disabled={isLoggingOut}
                  onClick={() =>
                    logout(undefined, {
                      onSuccess: () => {
                        setMenuOpen(false);
                        navigate({ to: ROUTES.LOGIN, replace: true });
                      },
                    })
                  }
                  className="flex items-center justify-between rounded-2xl bg-primary px-4 py-3.5 text-left text-white shadow-sm transition-colors hover:opacity-90 disabled:opacity-60"
                >
                  <span className="flex items-center gap-3">
                    <PowerIcon className="h-4 w-4 shrink-0" />
                    <span className="text-sm font-medium">
                      {isLoggingOut ? "Déconnexion..." : "Se déconnecter"}
                    </span>
                  </span>
                  <ChevronIcon className="h-3 w-2 shrink-0" />
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

function BurgerIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <rect x="2" y="4" width="16" height="2" rx="1" />
      <rect x="2" y="9" width="16" height="2" rx="1" />
      <rect x="2" y="14" width="16" height="2" rx="1" />
    </svg>
  );
}
